import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "../../icons";
import { Remove as RemoveIcon } from "../../icons";
import { Schedule as ScheduleIcon } from "../../icons";
import { DeliveryDining as DeliveryDiningIcon } from "../../icons";
import { ShoppingBag as ShoppingBagIcon } from "../../icons";
import { LocationOn as LocationOnIcon } from "../../icons";
import { Edit as EditIcon } from "../../icons";
import { Search as SearchIcon } from "../../icons";
import { MyLocation as MyLocationIcon } from "../../icons";
import { geocodeAddress, reverseGeocodeAddress } from "../../utils/geo";
import { auth } from "../../firebase";
import PageHeader from "../PageHeader";
import SheetDragHandle from "../../components/SheetDragHandle";
import { pageContentSx, PAGE_CONTENT_MAX_WIDTH } from "../../pageLayout";
import {
  categoryOf,
  formatMoney,
  isRestaurantOpen,
  loadCart,
  placeFoodOrder,
  storeCart,
  storeKind,
  subscribeMenu,
  subscribeRestaurant,
} from "./foodUtils";

import SearchClearAdornment from "../../components/SearchClearAdornment";
const CONTACT_KEY = "notiline_food_contact";

const loadContact = () => {
  try {
    return JSON.parse(localStorage.getItem(CONTACT_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const saveContact = (contact) => {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify(contact));
  } catch {
    // Sin almacenamiento: la próxima vez se vuelve a completar.
  }
};

const QtyControl = ({ value, onChange, min = 0 }) => (
  <Box sx={{ display: "flex", alignItems: "center", border: 1, borderColor: "divider", borderRadius: 999 }}>
    <IconButton size="small" onClick={() => onChange(Math.max(min, value - 1))} aria-label="Quitar uno">
      <RemoveIcon fontSize="small" />
    </IconButton>
    <Typography sx={{ minWidth: 24, textAlign: "center", fontWeight: 600 }}>{value}</Typography>
    <IconButton size="small" onClick={() => onChange(Math.min(50, value + 1))} aria-label="Agregar uno">
      <AddIcon fontSize="small" />
    </IconButton>
  </Box>
);

const RestaurantPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const uid = auth.currentUser?.uid;

  const [restaurant, setRestaurant] = useState(undefined);
  const [menu, setMenu] = useState(null);
  const [cart, setCart] = useState(() => loadCart(restaurantId));
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQty, setItemQty] = useState(1);
  const [itemNotes, setItemNotes] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  const savedContact = useMemo(loadContact, []);
  const [mode, setMode] = useState("delivery");
  const [address, setAddress] = useState(savedContact.address || "");
  // Coordenadas del destino para el mapa del recorrido (ver FoodOrderMap).
  const [deliveryLocation, setDeliveryLocation] = useState(savedContact.location || null);
  const [locating, setLocating] = useState(false);
  const [phone, setPhone] = useState(savedContact.phone || "");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => subscribeRestaurant(restaurantId, setRestaurant, () => setRestaurant(null)), [restaurantId]);
  useEffect(() => subscribeMenu(restaurantId, setMenu, () => setMenu([])), [restaurantId]);
  useEffect(() => storeCart(restaurantId, cart), [restaurantId, cart]);

  useEffect(() => {
    if (restaurant?.delivery === false) setMode("takeaway");
  }, [restaurant?.delivery]);

  // Si un plato se borró o se pausó, sale del carrito.
  useEffect(() => {
    if (!menu) return;
    const available = new Set(menu.filter((m) => m.available !== false).map((m) => m.id));
    setCart((prev) => (prev.every((line) => available.has(line.id)) ? prev : prev.filter((line) => available.has(line.id))));
  }, [menu]);

  const menuById = useMemo(() => Object.fromEntries((menu || []).map((m) => [m.id, m])), [menu]);

  const isMarket = storeKind(restaurant) === "market";

  const sections = useMemo(() => {
    const groups = new Map();
    const fold = (value) => String(value || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const term = fold(productSearch.trim());
    (menu || [])
      .filter((item) => !term || fold(`${item.name} ${item.description || ""}`).includes(term))
      .forEach((item) => {
      const name = item.section?.trim() || (isMarket ? "Productos" : "Menú");
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(item);
    });
    return [...groups.entries()];
  }, [menu, productSearch, isMarket]);

  const lines = cart.map((line) => ({ ...line, item: menuById[line.id] })).filter((line) => line.item);
  const itemCount = lines.reduce((sum, line) => sum + line.qty, 0);
  const subtotal = lines.reduce((sum, line) => sum + (Number(line.item.price) || 0) * line.qty, 0);
  const deliveryFee = mode === "delivery" ? Number(restaurant?.deliveryFee) || 0 : 0;
  const minOrder = Number(restaurant?.minOrder) || 0;
  const open = isRestaurantOpen(restaurant);
  const isOwner = restaurant && uid === restaurant.uid;

  const openItem = (item) => {
    setSelectedItem(item);
    setItemQty(1);
    setItemNotes("");
  };

  const addSelectedItem = () => {
    const notesText = itemNotes.trim();
    setCart((prev) => {
      const index = prev.findIndex((line) => line.id === selectedItem.id && (line.notes || "") === notesText);
      if (index >= 0) {
        const next = [...prev];
        next[index] = { ...next[index], qty: Math.min(50, next[index].qty + itemQty) };
        return next;
      }
      return [...prev, { id: selectedItem.id, qty: itemQty, notes: notesText }];
    });
    setSelectedItem(null);
  };

  const setLineQty = (index, qty) =>
    setCart((prev) => (qty <= 0 ? prev.filter((_, i) => i !== index) : prev.map((line, i) => (i === index ? { ...line, qty } : line))));

  const useMyLocation = () => {
    if (!navigator.geolocation) return setError("Tu dispositivo no permite obtener la ubicación.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const location = { lat: coords.latitude, lng: coords.longitude };
        setDeliveryLocation(location);
        const found = await reverseGeocodeAddress(location.lat, location.lng);
        if (found) setAddress(found);
        setLocating(false);
      },
      () => {
        setError("No pudimos obtener tu ubicación. Escribí la dirección.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const submitOrder = async () => {
    setError("");
    if (mode === "delivery" && !address.trim()) return setError("Completá la dirección de entrega.");
    if (!phone.trim()) return setError("Dejá un teléfono para que el local te pueda avisar.");
    setPlacing(true);
    try {
      let location = deliveryLocation;
      if (mode === "delivery" && !location) {
        // La ciudad del local ubica la dirección en el país correcto.
        const found = await geocodeAddress([address.trim(), restaurant?.city].filter(Boolean).join(", "));
        if (found) location = { lat: found.latitude, lng: found.longitude };
      }
      const orderId = await placeFoodOrder({
        deliveryLocation: mode === "delivery" ? location : null,
        restaurantId,
        items: cart.map(({ id, qty, notes: lineNotes }) => ({ id, qty, notes: lineNotes })),
        mode,
        address: address.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
        paymentMethod,
      });
      saveContact({ address: address.trim(), phone: phone.trim(), location: mode === "delivery" ? location : savedContact.location });
      setCart([]);
      navigate(`/pedidos/orden/${orderId}`, { replace: true });
    } catch (err) {
      setError(err?.message || "No se pudo hacer el pedido. Probá de nuevo.");
    } finally {
      setPlacing(false);
    }
  };

  if (restaurant === null) {
    return (
      <>
        <PageHeader title="Pedidos" />
        <Box sx={{ ...pageContentSx, textAlign: "center", py: 8 }}>
          <Typography variant="h6">Este local ya no está disponible.</Typography>
          <Button component={Link} to="/pedidos" sx={{ mt: 2 }} variant="contained">Ver otros locales</Button>
        </Box>
      </>
    );
  }

  const category = categoryOf(restaurant?.category);

  return (
    <>
      <PageHeader
        title={restaurant?.name || ""}
        subtitle={restaurant ? category.label : ""}
        loading={restaurant === undefined}
        rightSlot={isOwner ? (
          <IconButton color="inherit" component={Link} to="/pedidos/mi-local" aria-label="Editar mi local">
            <EditIcon />
          </IconButton>
        ) : null}
      />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, px: { xs: 0, md: 2 }, pb: itemCount ? 14 : 4 }}>
        <Box sx={{ position: "relative", height: { xs: 180, sm: 240 }, bgcolor: "action.hover", borderRadius: { xs: 0, md: 3 }, overflow: "hidden", mt: { xs: 0, md: 2 } }}>
          {restaurant === undefined ? (
            <Skeleton variant="rectangular" height="100%" />
          ) : restaurant.coverPhoto ? (
            <Box component="img" src={restaurant.coverPhoto} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>{category.emoji}</Box>
          )}
        </Box>

        <Box sx={{ px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, mt: -4 }}>
            <Avatar src={restaurant?.logo} sx={{ width: 76, height: 76, border: "4px solid", borderColor: "background.paper", bgcolor: "primary.main", fontSize: 36 }}>
              {category.emoji}
            </Avatar>
            <Chip
              label={open ? "Abierto" : "Cerrado"}
              color={open ? "success" : "default"}
              size="small"
              sx={{ mb: 1, fontWeight: 600 }}
            />
          </Box>
          {restaurant && (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>{restaurant.name}</Typography>
              {restaurant.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                  {restaurant.description}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5, color: "text.secondary" }}>
                {restaurant.deliveryTime && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" />
                    <Typography variant="body2">{restaurant.deliveryTime}</Typography>
                  </Box>
                )}
                {restaurant.delivery !== false && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <DeliveryDiningIcon fontSize="small" />
                    <Typography variant="body2">
                      {Number(restaurant.deliveryFee) > 0 ? `Envío ${formatMoney(restaurant.deliveryFee, restaurant?.currency)}` : "Envío gratis"}
                    </Typography>
                  </Box>
                )}
                {restaurant.takeaway !== false && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ShoppingBagIcon fontSize="small" />
                    <Typography variant="body2">Retiro en el local</Typography>
                  </Box>
                )}
                {minOrder > 0 && <Typography variant="body2">Mínimo {formatMoney(minOrder, restaurant?.currency)}</Typography>}
              </Box>
              {restaurant.address && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, color: "text.secondary" }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">{restaurant.address}</Typography>
                </Box>
              )}
              {(restaurant.openTime && restaurant.closeTime) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Horario: {restaurant.openTime} a {restaurant.closeTime}
                </Typography>
              )}
              {!open && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  El local no está tomando pedidos ahora. Podés mirar el menú y pedir cuando abra.
                </Alert>
              )}
            </>
          )}

          {(isMarket || (menu?.length || 0) > 12) && (
            <TextField
              fullWidth
              size="small"
              placeholder={isMarket ? "Buscar productos" : "Buscar en el menú"}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              autoComplete="off"
              sx={{ mt: 2, "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
              InputProps={{ endAdornment: <SearchClearAdornment value={productSearch} onChange={(e) => setProductSearch(e.target.value)} />, startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          )}

          {sections.length > 1 && (
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", mt: 2, pb: 0.5, position: "sticky", top: 76, zIndex: 2, bgcolor: "background.default", py: 1, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
              {sections.map(([name], index) => (
                <Chip
                  key={name}
                  label={name}
                  variant="outlined"
                  onClick={() => document.getElementById(`menu-section-${index}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                />
              ))}
            </Box>
          )}

          {menu === null && Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={96} sx={{ mt: 2, borderRadius: 2 }} />
          ))}
          {menu && !menu.length && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              {isMarket ? "Este local todavía no cargó sus productos." : "Este local todavía no cargó su menú."}
            </Typography>
          )}

          {menu?.length > 0 && !sections.length && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No encontramos “{productSearch}”.
            </Typography>
          )}

          {sections.map(([name, items], index) => (
            <Box key={name} id={`menu-section-${index}`} sx={{ scrollMarginTop: 140 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 3, mb: 1 }}>{name}</Typography>
              {items.map((item) => {
                const unavailable = item.available === false;
                return (
                  <Box
                    key={item.id}
                    onClick={() => !unavailable && openItem(item)}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      cursor: unavailable ? "default" : "pointer",
                      opacity: unavailable ? 0.5 : 1,
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                        >
                          {item.description}
                        </Typography>
                      )}
                      <Typography sx={{ fontWeight: 700, mt: 0.5 }}>
                        {unavailable ? "Sin stock" : formatMoney(item.price, restaurant?.currency)}
                      </Typography>
                    </Box>
                    {item.photo && (
                      <Box component="img" src={item.photo} alt="" loading="lazy" sx={{ width: 96, height: 96, borderRadius: 2, objectFit: "cover", flexShrink: 0 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Container>

      {itemCount > 0 && (
        <Box sx={{ position: "fixed", left: "var(--sidebar-w, 0px)", right: 0, bottom: 0, p: 2, zIndex: 1200, pointerEvents: "none" }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => setCartOpen(true)}
            sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH - 32, mx: "auto", display: "flex", borderRadius: 999, py: 1.5, pointerEvents: "auto", boxShadow: 6 }}
          >
            <Box component="span" sx={{ bgcolor: "rgba(255,255,255,0.25)", borderRadius: 999, px: 1.25, mr: 1.5, fontWeight: 700 }}>{itemCount}</Box>
            <Box component="span" sx={{ flexGrow: 1, textAlign: "left", fontWeight: 700 }}>Ver mi pedido</Box>
            <Box component="span" sx={{ fontWeight: 700 }}>{formatMoney(subtotal, restaurant?.currency)}</Box>
          </Button>
        </Box>
      )}

      <Dialog open={!!selectedItem} onClose={() => setSelectedItem(null)} fullWidth maxWidth="xs">
        {selectedItem && (
          <>
            {selectedItem.photo && (
              <Box component="img" src={selectedItem.photo} alt="" sx={{ width: "100%", maxHeight: 260, objectFit: "cover" }} />
            )}
            <DialogContent>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{selectedItem.name}</Typography>
              {selectedItem.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-line" }}>
                  {selectedItem.description}
                </Typography>
              )}
              <Typography sx={{ fontWeight: 700, mt: 1 }}>{formatMoney(selectedItem.price, restaurant?.currency)}</Typography>
              <TextField
                fullWidth
                size="small"
                label="Aclaraciones (opcional)"
                placeholder="Ej.: sin cebolla"
                value={itemNotes}
                onChange={(e) => setItemNotes(e.target.value.slice(0, 200))}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <QtyControl value={itemQty} onChange={setItemQty} min={1} />
              <Button variant="contained" onClick={addSelectedItem} sx={{ flexGrow: 1, borderRadius: 999 }}>
                Agregar {formatMoney((Number(selectedItem.price) || 0) * itemQty, restaurant?.currency)}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Drawer
        anchor="bottom"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "92vh" } }}
      >
        <Box sx={{ pt: 1.5, pb: 3, px: 2, overflowY: "auto", width: "100%", maxWidth: 560, mx: "auto", boxSizing: "border-box" }}>
          <SheetDragHandle onClose={() => setCartOpen(false)} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Tu pedido</Typography>
          <Typography variant="body2" color="text.secondary">{restaurant?.name}</Typography>

          {lines.map((line, index) => (
            <Box key={`${line.id}-${line.notes}`} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>{line.item.name}</Typography>
                {line.notes && <Typography variant="caption" color="text.secondary">{line.notes}</Typography>}
                <Typography variant="body2">{formatMoney((Number(line.item.price) || 0) * line.qty, restaurant?.currency)}</Typography>
              </Box>
              <QtyControl value={line.qty} onChange={(qty) => setLineQty(index, qty)} />
            </Box>
          ))}

          {restaurant?.delivery !== false && restaurant?.takeaway !== false && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={mode}
              onChange={(_, value) => value && setMode(value)}
              sx={{ mt: 2 }}
            >
              <ToggleButton value="delivery"><DeliveryDiningIcon sx={{ mr: 1 }} />Envío</ToggleButton>
              <ToggleButton value="takeaway"><ShoppingBagIcon sx={{ mr: 1 }} />Retiro</ToggleButton>
            </ToggleButtonGroup>
          )}

          {mode === "delivery" ? (
            <>
              <TextField
                fullWidth
                size="small"
                label="Dirección de entrega"
                placeholder="Calle, número, piso/depto"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value.slice(0, 300));
                  setDeliveryLocation(null);
                }}
                sx={{ mt: 2 }}
              />
              <Button size="small" startIcon={locating ? <CircularProgress size={16} /> : <MyLocationIcon />} onClick={useMyLocation} disabled={locating} sx={{ mt: 0.5 }}>
                {deliveryLocation ? "Ubicación marcada ✓" : "Usar mi ubicación"}
              </Button>
            </>
          ) : (
            restaurant?.address && (
              <Alert severity="info" icon={<LocationOnIcon />} sx={{ mt: 2 }}>Retirás en {restaurant.address}</Alert>
            )
          )}
          <TextField fullWidth size="small" label="Teléfono" type="tel" value={phone} onChange={(e) => setPhone(e.target.value.slice(0, 40))} sx={{ mt: 2 }} />
          <TextField fullWidth size="small" label="Comentarios para el local (opcional)" value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))} multiline minRows={2} sx={{ mt: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>¿Cómo pagás?</Typography>
          <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            <FormControlLabel value="efectivo" control={<Radio size="small" />} label={mode === "delivery" ? "Efectivo al recibir" : "Efectivo al retirar"} />
            {restaurant?.transferAlias && (
              <FormControlLabel value="transferencia" control={<Radio size="small" />} label="Transferencia (verás el alias cuando el local acepte)" />
            )}
          </RadioGroup>

          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="text.secondary">Productos</Typography>
            <Typography>{formatMoney(subtotal, restaurant?.currency)}</Typography>
          </Box>
          {mode === "delivery" && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography color="text.secondary">Envío</Typography>
              <Typography>{deliveryFee ? formatMoney(deliveryFee, restaurant?.currency) : "Gratis"}</Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Typography sx={{ fontWeight: 800 }}>Total</Typography>
            <Typography sx={{ fontWeight: 800 }}>{formatMoney(subtotal + deliveryFee, restaurant?.currency)}</Typography>
          </Box>

          {subtotal < minOrder && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              El pedido mínimo es de {formatMoney(minOrder, restaurant?.currency)}. Te faltan {formatMoney(minOrder - subtotal, restaurant?.currency)}.
            </Alert>
          )}
          {!open && <Alert severity="info" sx={{ mt: 2 }}>El local está cerrado ahora.</Alert>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={placing || !open || !lines.length || subtotal < minOrder}
            onClick={submitOrder}
            sx={{ mt: 2, borderRadius: 999, py: 1.25, fontWeight: 700 }}
          >
            {placing ? <CircularProgress size={24} color="inherit" /> : `Hacer pedido · ${formatMoney(subtotal + deliveryFee, restaurant?.currency)}`}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default RestaurantPage;
